import clsx from 'clsx';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';

interface OddsPoint {
  label?: string;
  away?: number;
  home?: number;
  total?: number;
  timestamp?: string;
}

export interface OddsSignalAnnotation {
  /** Index of the data point to annotate */
  dataIndex: number;
  /** Signal type determines icon and color */
  type: 'steam' | 'sharp' | 'injury' | 'weather' | 'close';
  /** Which odds line this signal is associated with */
  side?: 'away' | 'home' | 'total';
  label?: string;
}

const SIGNAL_COLORS: Record<OddsSignalAnnotation['type'], string> = {
  steam:   '#ef4444', // loss red — steam move
  sharp:   '#f59e0b', // warn amber — sharp money
  injury:  '#a78bfa', // purple — injury update
  weather: '#06b6d4', // info cyan — weather change
  close:   '#10b981', // win green — closing line
};

const SIGNAL_LABELS: Record<OddsSignalAnnotation['type'], string> = {
  steam:   'STM',
  sharp:   'SHP',
  injury:  'INJ',
  weather: 'WX',
  close:   'CLS',
};

interface OddsMovementChartProps {
  data: OddsPoint[];
  height?: number;
  showLines?: ('away' | 'home' | 'total')[];
  /** Optional signal annotations — vertical reference lines with colored labels */
  signals?: OddsSignalAnnotation[];
  className?: string;
}

const TOOLTIP_STYLE = {
  contentStyle: {
    background: '#0c1220',
    border: '1px solid #1f2d3d',
    borderRadius: 4,
    fontSize: 11,
  },
  labelStyle: { color: '#6b7280' },
  itemStyle: { color: '#e2e8f0' },
  cursor: { stroke: 'rgba(255,255,255,0.05)' },
};

/** Mini recharts line chart for odds movement over time with optional signal annotations */
export function OddsMovementChart({
  data,
  height = 80,
  showLines = ['away', 'home'],
  signals = [],
  className,
}: OddsMovementChartProps) {
  if (!data || data.length < 2) {
    return (
      <div
        className={clsx(
          'flex items-center justify-center text-[10px] text-gray-600 bg-surface-2 rounded',
          className,
        )}
        style={{ height }}
      >
        Insufficient data
      </div>
    );
  }

  return (
    <div className={className}>
      <div style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
            <XAxis
              dataKey="label"
              tick={{ fontSize: 9, fill: '#4b5563' }}
              tickLine={false}
              axisLine={false}
              interval="preserveStartEnd"
            />
            <YAxis
              tick={{ fontSize: 9, fill: '#4b5563' }}
              tickLine={false}
              axisLine={false}
              width={28}
            />
            <Tooltip
              {...TOOLTIP_STYLE}
              formatter={(v: number, name: string) => [
                v > 0 ? `+${v}` : `${v}`,
                name.charAt(0).toUpperCase() + name.slice(1),
              ]}
            />

            {/* Signal annotations — vertical reference lines */}
            {signals.map((sig, i) => {
              const xVal = data[sig.dataIndex]?.label ?? String(sig.dataIndex);
              const color = SIGNAL_COLORS[sig.type];
              const shortLabel = sig.label ?? SIGNAL_LABELS[sig.type];
              return (
                <ReferenceLine
                  key={i}
                  x={xVal}
                  stroke={color}
                  strokeWidth={1}
                  strokeDasharray="3 2"
                  opacity={0.7}
                  label={{
                    value: shortLabel,
                    position: 'insideTopLeft',
                    fontSize: 8,
                    fill: color,
                    offset: 2,
                  }}
                />
              );
            })}

            {showLines.includes('away') && (
              <Line
                type="monotone"
                dataKey="away"
                stroke="#3b82f6"
                strokeWidth={1.5}
                dot={false}
                name="away"
              />
            )}
            {showLines.includes('home') && (
              <Line
                type="monotone"
                dataKey="home"
                stroke="#10b981"
                strokeWidth={1.5}
                dot={false}
                name="home"
              />
            )}
            {showLines.includes('total') && (
              <Line
                type="monotone"
                dataKey="total"
                stroke="#f59e0b"
                strokeWidth={1.5}
                dot={false}
                name="total"
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Signal legend — only shown when signals are present */}
      {signals.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-1">
          {Array.from(new Set(signals.map((s) => s.type))).map((type) => (
            <div key={type} className="flex items-center gap-1">
              <span
                className="w-2 h-[1px] border-t border-dashed"
                style={{ borderColor: SIGNAL_COLORS[type] }}
              />
              <span className="text-[9px]" style={{ color: SIGNAL_COLORS[type] }}>
                {SIGNAL_LABELS[type]}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
